/**
 * DC «Коммерция» — настройка коммерческих блоков каталога клиентского приложения:
 * главный баннер (квест или своё медиа + трейлер), спецпредложения и
 * суперперсональное предложение. Источник правды — /api/commerce (perm `commerce`,
 * роль DC). Хук useCommerce: GET на маунте, PUT по «Сохранить».
 *
 * Стили формы — инлайновые токены (--role зелёный у DC), корень/состояния —
 * общий dcSections.module.css как у соседних секций.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useCommerce, type CommerceConfig, type CommerceOffer } from "../../hooks/useCommerce";
import css from "./dcSections.module.css";

const lbl: CSSProperties = { display: "block", fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-2)", margin: "0 0 4px" };
const inp: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "8px 10px", background: "var(--surface-1)", border: "1px solid var(--surface-3)", borderRadius: 7, color: "var(--text-1)", fontSize: 13, fontFamily: "inherit" };
const area: CSSProperties = { ...inp, minHeight: 60, resize: "vertical", lineHeight: 1.45 };
const field: CSSProperties = { margin: "0 0 10px" };
const group: CSSProperties = { margin: "0 0 20px" };
const groupH: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--role)", margin: "0 0 12px", paddingBottom: 6, borderBottom: "1px solid var(--surface-3)" };
const cardBox: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 9, padding: 11, margin: "0 0 9px", background: "var(--surface-1)" };
const miniBtn: CSSProperties = { padding: "6px 11px", borderRadius: 7, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 12, cursor: "pointer" };
const segOn: CSSProperties = { ...miniBtn, background: "var(--role)", color: "#06121a", borderColor: "var(--role)", fontWeight: 700 };
const intro: CSSProperties = { fontSize: 12, lineHeight: 1.5, color: "var(--text-2)", margin: "0 0 16px" };
const hint: CSSProperties = { fontSize: 11, color: "var(--text-3)", margin: "4px 0 0" };
const saveBar: CSSProperties = { position: "sticky", bottom: 0, display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 0", marginTop: 4, background: "var(--surface-1)", borderTop: "1px solid var(--surface-3)" };

export function DcCommerceSection({ onToast }: { onToast: (m: string) => void }) {
  const { config, loading, error, reload, save } = useCommerce();
  const [draft, setDraft] = useState<CommerceConfig | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (config) setDraft(JSON.parse(JSON.stringify(config)) as CommerceConfig);
  }, [config]);

  const dirty = useMemo(
    () => !!config && !!draft && JSON.stringify(config) !== JSON.stringify(draft),
    [config, draft],
  );

  const setFeat = (patch: Partial<CommerceConfig["featured"]>) =>
    setDraft((d) => (d ? { ...d, featured: { ...d.featured, ...patch } } : d));
  const setOffer = (i: number, patch: Partial<CommerceOffer>) =>
    setDraft((d) => (d ? { ...d, offers: d.offers.map((o, j) => (j === i ? { ...o, ...patch } : o)) } : d));
  const addOffer = () =>
    setDraft((d) => (d ? { ...d, offers: [...d.offers, { id: `offer-${Date.now()}`, title: "", description: "" }] } : d));
  const delOffer = (i: number) =>
    setDraft((d) => (d ? { ...d, offers: d.offers.filter((_, j) => j !== i) } : d));
  const setPersonal = (patch: Partial<NonNullable<CommerceConfig["personalOffer"]>>) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            personalOffer: {
              title: d.personalOffer?.title ?? "",
              description: d.personalOffer?.description ?? "",
              imageUrl: d.personalOffer?.imageUrl,
              ...patch,
            },
          }
        : d,
    );
  const togglePersonal = (on: boolean) =>
    setDraft((d) => (d ? { ...d, personalOffer: on ? d.personalOffer ?? { title: "", description: "" } : null } : d));

  const onSave = useCallback(async () => {
    if (!draft || !dirty || busy) return;
    setBusy(true);
    const res = await save(draft);
    setBusy(false);
    onToast(res.ok ? "Коммерция сохранена" : `Ошибка: ${res.error ?? "—"}`);
  }, [draft, dirty, busy, save, onToast]);

  if (loading) return <div className={css.main}><div className={css.loading}>Загрузка…</div></div>;
  if (error || !config || !draft) {
    return (
      <div className={css.main}>
        <div className={css.err}>Не удалось загрузить коммерцию{error ? `: ${error}` : ""}</div>
        <div style={{ marginTop: 10 }}>
          <button type="button" style={miniBtn} onClick={reload}>Повторить</button>
        </div>
      </div>
    );
  }

  const f = draft.featured;
  const canSave = dirty && !busy;
  const saveBtn: CSSProperties = {
    padding: "9px 20px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700,
    background: canSave ? "var(--role)" : "var(--surface-3)", color: canSave ? "#06121a" : "var(--text-3)",
    cursor: canSave ? "pointer" : "default",
  };

  return (
    <div className={css.main}>
      <div style={intro}>
        Эти блоки видны клиентам в каталоге квестов. Картинки и трейлеры задаются ссылкой (URL).
        Сохранение применяется сразу ко всему клиентскому приложению.
      </div>

      <div style={group}>
        <div style={groupH}>Главный баннер</div>
        <div style={{ display: "flex", gap: 6, margin: "0 0 10px" }}>
          <button type="button" style={f.mode === "quest" ? segOn : miniBtn} onClick={() => setFeat({ mode: "quest" })}>Квест</button>
          <button type="button" style={f.mode === "custom" ? segOn : miniBtn} onClick={() => setFeat({ mode: "custom" })}>Своё</button>
        </div>
        {f.mode === "quest" ? (
          <div style={field}>
            <label style={lbl}>ID квеста</label>
            <input style={inp} value={f.questId ?? ""} placeholder="psih_m" onChange={(e) => setFeat({ questId: e.target.value })} />
            <div style={hint}>Например: psih_m, sudn, taro, motl, babk, fnaf, durk.</div>
          </div>
        ) : (
          <>
            <div style={field}><label style={lbl}>Заголовок</label><input style={inp} value={f.title ?? ""} onChange={(e) => setFeat({ title: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Подзаголовок</label><input style={inp} value={f.subtitle ?? ""} onChange={(e) => setFeat({ subtitle: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Картинка (URL)</label><input style={inp} value={f.imageUrl ?? ""} onChange={(e) => setFeat({ imageUrl: e.target.value })} /></div>
          </>
        )}
        <div style={field}><label style={lbl}>Трейлер (URL, необязательно)</label><input style={inp} value={f.trailerUrl ?? ""} placeholder="https://…" onChange={(e) => setFeat({ trailerUrl: e.target.value })} /></div>
      </div>

      <div style={group}>
        <div style={groupH}>Спецпредложения ({draft.offers.length})</div>
        {draft.offers.map((o, i) => (
          <div key={o.id} style={cardBox}>
            <div style={field}><label style={lbl}>Название</label><input style={inp} value={o.title} onChange={(e) => setOffer(i, { title: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Описание</label><textarea style={area} value={o.description} onChange={(e) => setOffer(i, { description: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Картинка (URL, необязательно)</label><input style={inp} value={o.imageUrl ?? ""} onChange={(e) => setOffer(i, { imageUrl: e.target.value })} /></div>
            <button type="button" style={{ ...miniBtn, color: "var(--danger, #ff6b6b)" }} onClick={() => delOffer(i)}>Удалить</button>
          </div>
        ))}
        <button type="button" style={miniBtn} onClick={addOffer}>+ Добавить предложение</button>
      </div>

      <div style={group}>
        <div style={groupH}>Суперперсональное предложение</div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 10px", fontSize: 13, color: "var(--text-1)", cursor: "pointer" }}>
          <input type="checkbox" checked={!!draft.personalOffer} onChange={(e) => togglePersonal(e.target.checked)} />
          Показывать блок
        </label>
        {draft.personalOffer ? (
          <>
            <div style={field}><label style={lbl}>Заголовок</label><input style={inp} value={draft.personalOffer.title} onChange={(e) => setPersonal({ title: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Описание</label><textarea style={area} value={draft.personalOffer.description} onChange={(e) => setPersonal({ description: e.target.value })} /></div>
            <div style={field}><label style={lbl}>Картинка (URL, необязательно)</label><input style={inp} value={draft.personalOffer.imageUrl ?? ""} onChange={(e) => setPersonal({ imageUrl: e.target.value })} /></div>
          </>
        ) : null}
      </div>

      <div style={saveBar}>
        <button type="button" style={saveBtn} disabled={!canSave} onClick={onSave}>
          {busy ? "Сохранение…" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}
